import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {useFleetFeatures} from '@/entities/device/fleet';
import {useAuthStore} from '@/entities/session/auth-store';
import {contextToneAndLabel} from '@/entities/session/labels';
import type {AppContext} from '@/entities/session/contexts';
import {LANG_LABELS, SUPPORTED_LANGS, setLang, type Lang} from '@/shared/config/i18n';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {InstallCard} from '@/widgets/pwa/InstallCard';
import {Avatar} from '@/shared/ui/avatar';
import {AppButton} from '@/shared/ui/button';
import {Icon} from '@/shared/ui/icons';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {Page} from '@/shared/ui/page';
import {SHOW_ESTATE_GROUP, SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {Sheet} from '@/shared/ui/sheet';
import {StatusDot} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {THEME_MODES, useThemeMode, useThemeModeStore} from '@/shared/ui/theme-mode';

/**
 * «Настройки» — то же содержимое, что было в «Ещё», но разложенное по группам.
 *
 * ## Что было не так с «Ещё»
 *
 * Пятнадцать строк подряд, без единого заголовка между ними: отделы, участники,
 * ведомость, телеграм, камеры, уведомления, реквизиты, профили терминалов, интеграция,
 * журнал, профиль, безопасность, пуши. Половина этого списка — управление КОМПАНИЕЙ,
 * половина — свойства ЧИТАЮЩЕГО, и по виду они не отличались ничем. Найти нужное
 * можно было только прочитав все пятнадцать.
 *
 * Здесь их четыре группы, и у каждой есть один вопрос, на который она отвечает:
 *
 *      Компания      кто у нас есть и как называется наша организация
 *      Уведомления   кому и о чём сообщать
 *      Оборудование  как настроены терминалы и чем к ним подключаются
 *      Аккаунт       я сам: вход, безопасность, язык, тема
 *
 * Двух пунктов в списке больше нет вовсе. «Камеры» уехали в «Устройства», «Ведомость»
 * — в «Посещаемость», «Журнал событий» — в «События». Все три были не настройками, а
 * разделами, попавшими сюда потому, что вкладок не хватило.
 *
 * ## Что решает вид контекста, а что — право
 *
 * Какие группы показать, решает ВИД активного контекста, а не роль: у сотрудника нет
 * компании, чтобы ей управлять, а у личного пространства нет ни участников, ни
 * отделов. Кабинет никогда не прячет строку из-за того, что политика Casbin МОЖЕТ
 * отказать, — про отказ узнают из `403`, как и везде (docs/api/conventions.md §6).
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'Settings'>;

const styles = StyleSheet.create({
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    padding: spacing(4),
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.xl,
  },
  identityText: {flex: 1, minWidth: 0, gap: 2},
  contextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    padding: spacing(4),
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.lg,
  },
  contextText: {flex: 1, gap: 2},
  contexts: {gap: spacing(2)},
  check: {
    width: 24,
    height: 24,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    padding: spacing(4),
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.lg,
  },
  optionText: {flex: 1, gap: 2},
});

/** Строка листа выбора: что это, и галочка у того, что сейчас в силе. */
function SheetOption({
  title,
  selected,
  onPress,
  testID,
}: {
  title: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{selected}}
      onPress={onPress}
      style={({pressed}) => [
        styles.option,
        {
          backgroundColor: selected ? p.accent : pressed ? p.muted : p.card,
          borderColor: selected ? p.primary : p.border,
        },
      ]}>
      <View style={styles.optionText}>
        <AppText variant="subtitle">{title}</AppText>
      </View>
      {selected ? (
        <View style={[styles.check, {backgroundColor: p.primary}]}>
          <Icon name="check" size={14} color={p.primaryForeground} />
        </View>
      ) : null}
    </Pressable>
  );
}

export function SettingsScreen({navigation}: Props) {
  const {t, i18n} = useTranslation();
  const p = useTheme();
  // На широком окне разделы стоят в колонке слева (`SettingsNav`), и повторять их
  // строками справа значило бы напечатать одно оглавление дважды. Здесь остаётся то,
  // чего в колонке нет: кто я, мои контексты, язык и тема, выход.
  const {isDesktop} = useBreakpoint();

  const account = useAuthStore(s => s.account);
  const contexts = useAuthStore(s => s.contexts);
  const activeContext = useAuthStore(s => s.activeContext);
  const setActiveContext = useAuthStore(s => s.setActiveContext);
  const signOut = useAuthStore(s => s.signOut);

  const themeMode = useThemeMode();
  const setThemeMode = useThemeModeStore(s => s.setMode);
  const lang = (i18n.resolvedLanguage ?? 'ru') as Lang;

  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const isActive = (c: AppContext) =>
    activeContext != null &&
    c.kind === activeContext.kind &&
    c.companyId === activeContext.companyId;

  const kind = activeContext?.kind ?? 'home';
  const isCompany = kind === 'company';
  // Без терминалов «Отделы» — своя вкладка, а календарю считать нечего (см. SettingsNav).
  const {attendance} = useFleetFeatures().features;
  // У личного пространства есть терминалы и журнал, но нет ни персонала, ни
  // участников, ни подписок в Telegram: его «компания» существует только затем,
  // чтобы владеть устройствами.
  const hasEstate = SHOW_ESTATE_GROUP && (kind === 'company' || kind === 'home');

  return (
    <Page
      sideNav={<SettingsNav />}
      scroll
      width="form"
      title={t('nav.settings')}
      subtitle={isDesktop ? t('settings.overviewHint') : undefined}
      testID="settings-screen">
      <Section>
        <Pressable
          testID="settings-identity"
          accessibilityRole="button"
          accessibilityLabel={t('settings.account')}
          onPress={() => navigation.navigate('Account')}
          style={({pressed}) => [
            styles.identity,
            {backgroundColor: pressed ? p.accent : p.card, borderColor: p.border},
          ]}>
          <Avatar brand name={account?.fullName ?? '?'} size={48} />
          <View style={styles.identityText}>
            <AppText variant="title" numberOfLines={1}>
              {account?.fullName ?? '—'}
            </AppText>
            <AppText variant="caption" numberOfLines={1} testID="profile-email">
              {account?.email ?? '—'}
            </AppText>
          </View>
          <Icon name="chevron" size={16} color={p.mutedForeground} />
        </Pressable>
      </Section>

      {/* Все контексты аккаунта, а не только активный. Это единственный экран, где
          «кто я сейчас» — вопрос, на который отвечают осознанно; чип в шапке это
          ярлык, а вот это карта. Выбранное сказано дважды — заливкой И галочкой,
          чтобы ответ пережил читателя, который не отличает бирюзовый от серого. */}
      {contexts.length > 1 ? (
        <Section title={t('profile.contexts')}>
          <View style={styles.contexts}>
            {contexts.map(c => {
              const badge = contextToneAndLabel(c);
              const active = isActive(c);
              return (
                <Pressable
                  key={`${c.kind}:${c.companyId}`}
                  testID={`context-${c.kind}`}
                  accessibilityRole="button"
                  accessibilityState={{selected: active}}
                  onPress={() => setActiveContext(c)}
                  style={({pressed}) => [
                    styles.contextRow,
                    {
                      backgroundColor: active ? p.accent : pressed ? p.muted : p.card,
                      borderColor: active ? p.primary : p.border,
                    },
                  ]}>
                  <StatusDot tone={badge.tone} size={9} />
                  <View style={styles.contextText}>
                    <AppText variant="subtitle">{c.title}</AppText>
                    <AppText variant="caption">
                      {t(badge.kindKey)}
                      {c.kind === 'company' ? ` · ${t(`context.role.${c.role}`)}` : ''}
                    </AppText>
                  </View>
                  {active ? (
                    <View style={[styles.check, {backgroundColor: p.primary}]}>
                      <Icon name="check" size={15} color={p.primaryForeground} />
                    </View>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </Section>
      ) : null}

      {isCompany && !isDesktop ? (
        <Section title={t('settings.groupCompany')} hint={t('settings.groupCompanyHint')}>
          <RowGroup>
            {attendance ? (
              <ListRow
                icon="building"
                title={t('nav.departments')}
                subtitle={t('settings.departmentsHint')}
                onPress={() => navigation.navigate('Departments')}
                testID="settings-departments"
              />
            ) : null}
            <ListRow
              icon="people"
              title={t('members.title')}
              subtitle={t('settings.membersHint')}
              onPress={() => navigation.navigate('Members')}
              testID="settings-members"
            />
            <ListRow
              icon="settings"
              title={t('settings.companyDetails')}
              subtitle={t('settings.companyDetailsHint')}
              onPress={() => navigation.navigate('CompanySettings')}
              testID="settings-company"
            />
            {attendance ? (
              <ListRow
                icon="calendar"
                title={t('nav.calendar')}
                subtitle={t('calendar.subtitle')}
                onPress={() => navigation.navigate('CalendarSettings')}
                testID="settings-calendar"
              />
            ) : null}
          </RowGroup>
        </Section>
      ) : null}

      {isCompany && !isDesktop ? (
        <Section title={t('settings.groupNotifications')} hint={t('settings.groupNotificationsHint')}>
          <RowGroup>
            <ListRow
              icon="send"
              title={t('telegram.title')}
              subtitle={t('settings.telegramHint')}
              onPress={() => navigation.navigate('Telegram')}
              testID="settings-telegram"
            />
            <ListRow
              icon="bell"
              title={t('notifSettings.title')}
              subtitle={t('settings.notifRulesHint')}
              onPress={() => navigation.navigate('NotificationSettings')}
              testID="settings-notifications"
            />
          </RowGroup>
        </Section>
      ) : null}

      {hasEstate && !isDesktop ? (
        <Section title={t('settings.groupEstate')} hint={t('settings.groupEstateHint')}>
          <RowGroup>
            <ListRow
              icon="shield"
              title={t('more.terminalProfiles')}
              subtitle={t('settings.profilesHint')}
              onPress={() => navigation.navigate('TerminalProfiles')}
              testID="settings-profiles"
            />
            <ListRow
              icon="desktop"
              title={t('more.apiBridge')}
              subtitle={t('settings.apiBridgeHint')}
              onPress={() => navigation.navigate('ApiBridge')}
              testID="settings-api"
            />
          </RowGroup>
        </Section>
      ) : null}

      <Section title={t('settings.groupAccount')} hint={t('settings.groupAccountHint')}>
        <RowGroup>
          {!isDesktop ? (
            <ListRow
              icon="profile"
              title={t('settings.account')}
              subtitle={t('settings.accountHint')}
              onPress={() => navigation.navigate('Account')}
              testID="settings-account"
            />
          ) : null}
          {!isDesktop ? (
            <ListRow
              icon="lock"
              title={t('more.security')}
              subtitle={t('more.securityHint')}
              onPress={() => navigation.navigate('Security')}
              testID="settings-security"
            />
          ) : null}
          {!isDesktop ? (
            <ListRow
              icon="bell"
              title={t('push.title')}
              subtitle={t('settings.pushHint')}
              onPress={() => navigation.navigate('PushSettings')}
              testID="settings-push"
            />
          ) : null}
          {/* Язык и тема — свойства читающего, и лежат они там, где их ищут: в
              настройках аккаунта. На десктопе те же два переключателя есть в углу
              боковой панели, потому что там их держит любая консоль. */}
          <ListRow
            icon="language"
            title={t('account.language')}
            value={LANG_LABELS[lang]}
            onPress={() => setLangOpen(true)}
            testID="settings-language"
          />
          <ListRow
            icon="theme"
            title={t('account.theme')}
            value={t(`account.themeMode.${themeMode}`)}
            onPress={() => setThemeOpen(true)}
            testID="settings-theme"
          />
        </RowGroup>
      </Section>

      <Section>
        <InstallCard />
      </Section>

      <Section>
        <AppButton
          title={t('profile.signOut')}
          tone="outline"
          icon="logout"
          loading={leaving}
          onPress={() => {
            setLeaving(true);
            void signOut();
          }}
          testID="settings-sign-out"
        />
      </Section>

      <Sheet
        testID="language-sheet"
        visible={langOpen}
        onClose={() => setLangOpen(false)}
        title={t('account.language')}>
        {SUPPORTED_LANGS.map(code => (
          <SheetOption
            key={code}
            testID={`language-option-${code}`}
            // Каждый язык называет себя на себе.
            title={LANG_LABELS[code]}
            selected={lang === code}
            onPress={() => {
              void setLang(code);
              setLangOpen(false);
            }}
          />
        ))}
      </Sheet>

      <Sheet
        testID="theme-sheet"
        visible={themeOpen}
        onClose={() => setThemeOpen(false)}
        title={t('account.theme')}>
        {THEME_MODES.map(mode => (
          <SheetOption
            key={mode}
            testID={`theme-option-${mode}`}
            title={t(`account.themeMode.${mode}`)}
            selected={themeMode === mode}
            onPress={() => {
              setThemeMode(mode);
              setThemeOpen(false);
            }}
          />
        ))}
      </Sheet>
    </Page>
  );
}
